<template>
  <div>
    <div style="display: grid; grid-template-columns: repeat(4, 1fr)">
      <div
        v-for="index in 4"
        :key="`status${index}`"
        :data-testid="`status-${index}`"
        class="pt-1 flex flex-column justify-content-center gap-1"
        style="margin-top: -0.25rem; border-radius: var(--p-border-radius-md) !important"
        :class="{
          'cursor-pointer': !props.disabled,
          'selected-status': multiStatus.includes(index),
        }"
        @click.stop="select(index)"
      >
        <span class="h-100 flex justify-content-center">
          <StatusCircleComponent style="height: 1.75rem" :status="index" />
        </span>
        <p class="m-0 p-1 flex justify-content-center">
          {{ t(`components.TaskModal.pdcaCircle${index}`) }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import { computed, type PropType } from "vue";
import StatusCircleComponent from "./StatusCircleComponent.vue";
import { SQDCActionStatus } from "@/graphql.ts";

const props = defineProps({
  status: {
    required: true,
    type: [Number, String, Array] as PropType<number | SQDCActionStatus[] | number[]>,
  },
  multi: {
    required: false,
    type: Boolean,
    default: false,
  },
  disabled: {
    required: false,
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(["update:status"]);

if (props.multi !== Array.isArray(props.status)) {
  throw new Error(
    "if using StatusSelectComponent with multi, please provide an Array and vice versa",
  );
}

const statusOrder = [
  SQDCActionStatus.PLAN,
  SQDCActionStatus.DO,
  SQDCActionStatus.CHECK,
  SQDCActionStatus.ACT,
];

const multiStatus = computed<number[]>({
  get: (): number[] => {
    if (Array.isArray(props.status)) {
      return typeof props.status[0] === "number"
        ? (props.status as number[])
        : props.status.map((status) => statusOrder.indexOf(status as SQDCActionStatus) + 1);
    }
    return [
      typeof props.status === "number"
        ? props.status
        : statusOrder.indexOf(props.status as SQDCActionStatus) + 1,
    ];
  },
  set: (val: number[]) => {
    if (props.multi) {
      emit("update:status", val);
    } else {
      emit("update:status", val[0]);
    }
  },
});

function select(index: number) {
  if (props.disabled) return;
  // Check if multi mode
  if (props.multi && Array.isArray(props.status)) {
    // If yes, check if the status array already includes the selected value
    if (
      props.status
        .map((status) =>
          typeof status === "number" ? status : statusOrder.indexOf(status as SQDCActionStatus) + 1,
        )
        .includes(index)
    ) {
      // emit the status array without the selected value
      emit(
        "update:status",
        props.status.filter((value) => value !== index),
      );
    } else {
      // add the selected value and sort the array
      emit("update:status", [...props.status, index].sort());
    }
  } else {
    // No multi mode. Just select the index
    emit("update:status", index);
  }
}

const { t } = useTranslation();
</script>

<style scoped lang="scss">
.selected-status {
  background-color: var(--p-surface-200);
}
</style>
